import Global from '../global-selectors';

import { WeightUnit } from '../../enums/weight-unit';
import { DimensionUnit } from '../../enums/dimension-unit';
import { SellerSettings } from '../../enums/seller-settings/seller-settings';

export class SellerProperties {
  constructor({ state }) {
    if (!state) {
      throw new Error(`expected value for state, got: ${state}`);
    }

    this.state = state;
  }

  get sellerId() {
    const { state } = this;
    return Global.selectSellerSellerId(state);
  }

  getSellerSetting(settingName) {
    const { state } = this;
    const sellerSettings = Global.selectSellerSellerSettings(state) || {};

    return sellerSettings[settingName];
  }

  isSellerSettingEnabled(settingName) {
    const initialSettingValue = this.getSellerSetting(settingName);
    const settingValue = initialSettingValue && initialSettingValue.toLowerCase();

    if (settingValue === 'true') {
      return true;
    }
    if (settingValue === 'false') {
      return false;
    }

    return undefined;
  }

  get defaultCurrencyCode() {
    const { state } = this;
    return Global.selectSellerDefaultCurrency(state) || 'USD';
  }

  get isInTrial() {
    const { state } = this;
    const subscriptionPlanInfo = Global.selectSellerSubscriptionPlanInfo(state);

    if (!subscriptionPlanInfo) {
      return false;
    }

    if (subscriptionPlanInfo.planCode && subscriptionPlanInfo.planCode.toLowerCase() !== 'trial') {
      return false;
    }

    if (subscriptionPlanInfo.isExpired === undefined || subscriptionPlanInfo.isExpired === null) {
      return false;
    }

    return !subscriptionPlanInfo.isExpired;
  }

  get homeCountry() {
    const { state } = this;
    return Global.selectSellerHomeCountry(state);
  }

  get supportCountry() {
    const { state } = this;
    return Global.selectSellerSupportCountry(state);
  }

  get earlyAccessEnabled() {
    return this.isSellerSettingEnabled('EarlyAccessEnabled');
  }

  get isSetupComplete() {
    return this.isSellerSettingEnabled('SetupComplete');
  }

  get isInventoryEnabled() {
    return this.isSellerSettingEnabled('Inventory:TrackingEnabled');
  }

  get isShippedWhenLabelCreated() {
    const shippedWhenSetting = this.getSellerSetting('ShippedWhen');
    return shippedWhenSetting && shippedWhenSetting === 'LabelCreated';
  }

  get accountManager() {
    const { state } = this;
    return Global.selectSellerAccountManager(state);
  }

  get subscriptionPlanInfo() {
    const { state } = this;
    return Global.selectSellerSubscriptionPlanInfo(state);
  }

  get trialEndDate() {
    const { subscriptionPlanInfo } = this;
    return subscriptionPlanInfo.trialEndDate;
  }

  get company() {
    const { state } = this;
    return Global.selectSellerCompanyName(state);
  }

  get activationDate() {
    return this.getSellerSetting('activationDate');
  }

  get labelProviders() {
    const { state } = this;
    return Global.selectSellerLabelProviders(state);
  }

  get defaultDimensionUnit() {
    const value = this.getSellerSetting(SellerSettings.dimensionUnitOfMeasure.key);
    if ([DimensionUnit.Inch, DimensionUnit.Inches].includes(value)) {
      return DimensionUnit.Inches;
    }

    if ([DimensionUnit.Centimeter, DimensionUnit.Centimeters].includes(value)) {
      return DimensionUnit.Centimeters;
    }

    return DimensionUnit.Inches;
  }

  get defaultWeightUnit() {
    const value = this.getSellerSetting(SellerSettings.weightUnitOfMeasure.key);
    if ([WeightUnit.Gram, WeightUnit.Grams].includes(value)) {
      return WeightUnit.Grams;
    }

    if ([WeightUnit.Ounce, WeightUnit.Ounces].includes(value)) {
      return WeightUnit.Ounces;
    }

    return WeightUnit.Ounces;
  }

  get is3PLPlanEnabled() {
    const { state } = this;
    const { isExpired: isSubscriptionExpired, is3PlEnabled, threePlEnabledDate } = Global.selectSellerSubscriptionPlanInfo(state);

    if (isSubscriptionExpired || !threePlEnabledDate) {
      return false;
    }

    return is3PlEnabled;
  }
}
